import { Alert, Typography, Upload, notification } from "antd-v5";
import axios from "axios";
import message from "components/CustomMessage";
import { LoadingIcon } from "components/Icons";
import React, { useCallback, useState } from "react";
import GenericIntegrationAPI from "restAPI/GenericIntegrationAPI";
import { LogUploadSourceType } from "types/api/aiRiskMgmt";

import ACButton from "ac-components/ac-button/ACButton";

const { Dragger } = Upload;
const ACCEPTED_EXTENSIONS = ".json.gz,.jsonl.gz";

interface LogFileUploadProps {
  sourceType: LogUploadSourceType;
  label: string;
}

export default function LogFileUpload({
  sourceType,
  label,
}: LogFileUploadProps) {
  const [uploading, setUploading] = useState(false);

  const customRequest = useCallback(
    async ({ file, onSuccess, onError }: any) => {
      if (!file.name.endsWith(".json.gz") && !file.name.endsWith(".jsonl.gz")) {
        message.error("File must end with .json.gz or .jsonl.gz");
        onError?.(new Error("Invalid file extension"));
        return;
      }
      const notificationKey = `${sourceType}-upload-${Date.now()}`;
      let s3Key = "";
      let bucket = "";
      let s3UploadDone = false;
      try {
        setUploading(true);
        notification.info({
          key: notificationKey,
          message: "File upload in progress",
          icon: <LoadingIcon />,
          description: "Please do not refresh or close the browser.",
          duration: 0,
          closeIcon: <div />,
        });
        const presignResponse =
          await GenericIntegrationAPI.getLogUploadPresignedUrl({
            sourceType,
            fileName: file.name,
          }).promise;
        const { signedUrl, ...presignData } =
          presignResponse?.data ?? presignResponse;
        s3Key = presignData.s3Key;
        bucket = presignData.bucket;

        await axios({ method: "put", url: signedUrl, data: file });
        s3UploadDone = true;

        await GenericIntegrationAPI.notifyLogUploadComplete({
          sourceType,
          s3Key,
          bucket,
          fileName: file.name,
          fileSize: file.size,
          uploadStatus: "SUCCESS",
        }).promise;
        onSuccess?.("ok");
        message.success(
          "File uploaded successfully. It has been queued for processing.",
        );
      } catch (e) {
        if (s3Key && !s3UploadDone) {
          try {
            await GenericIntegrationAPI.notifyLogUploadComplete({
              sourceType,
              s3Key,
              bucket,
              fileName: file.name,
              fileSize: file.size,
              uploadStatus: "FAILED",
            }).promise;
          } catch {
            // Best-effort failure notification — don't block user error handling
          }
        }
        onError?.(e instanceof Error ? e : new Error("Upload failed"));
        if (!axios.isCancel(e)) {
          message.error("File upload failed. Please try again.");
        }
      } finally {
        setUploading(false);
        notification.destroy(notificationKey);
      }
    },
    [sourceType],
  );

  return (
    <div className="log-file-upload">
      <Alert
        type="info"
        className="m-b-sm"
        message={`Upload ${label} log files directly. Files will be uploaded to S3 for processing.`}
      />
      <Dragger
        multiple={false}
        showUploadList={false}
        customRequest={customRequest}
        disabled={uploading}
        accept={ACCEPTED_EXTENSIONS}
      >
        <p className="ant-upload-drag-icon">
          <ACButton type="primary" loading={uploading}>
            Upload Log File
          </ACButton>
        </p>
        <p className="ant-upload-text">
          Click or drag a file to this area to upload
        </p>
        <Typography.Text type="secondary">
          Supported formats: .json.gz, .jsonl.gz
        </Typography.Text>
      </Dragger>
    </div>
  );
}
